import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { DynamicCombobox } from '@shared/components/ui/DynamicCombobox';
import { Input } from '@shared/components/ui/input';
import type { ClientRuleConditionValueSelect } from '@modules/configuration/Rules/hooks/useClientRuleConditionValueSelect';
import { TFunction } from 'i18next';
import { ConditionFormRow, isCountryField, isIdField, isNumericField } from './exceptionConditionConfig';

interface ExceptionConditionValueFieldProps {
  row: ConditionFormRow;
  idx: number;
  countries: { id: number; name: string; iso2: string }[];
  productSelect: ClientRuleConditionValueSelect | null;
  packSelect: ClientRuleConditionValueSelect | null;
  onValueChange: (value: string) => void;
  t: TFunction;
  testIdPrefix: 'create-exception' | 'edit-exception';
}

export const ExceptionConditionValueField = ({
  row,
  idx,
  countries,
  productSelect,
  packSelect,
  onValueChange,
  t,
  testIdPrefix,
}: ExceptionConditionValueFieldProps) => {
  if (isCountryField(row)) {
    return (
      <Combobox
        options={countries.map((c) => ({ value: c.iso2, label: c.name }))}
        value={row.value}
        setValue={onValueChange}
        placeholder={t('specialRulesModal.countryPlaceholder')}
        dataTestId={`${testIdPrefix}-country-combobox-${idx}`}
      />
    );
  }

  if (isIdField(row)) {
    const scopedSelect = row.field === 'product_id' ? productSelect : packSelect;
    if (scopedSelect) {
      return (
        <DynamicCombobox
          value={row.value}
          setValue={onValueChange}
          placeholder={t(`specialRulesModal.selectors.${row.field}`)}
          searchFn={scopedSelect.searchFn}
          hasMore={scopedSelect.hasMore}
          resolveSelectedOption={scopedSelect.resolveSelected}
          emptyMessage={t('specialRulesModal.selectors.empty')}
          loadingMessage={t('specialRulesModal.selectors.loading')}
          dataTestId={`${testIdPrefix}-value-select-${row.field}-${idx}`}
        />
      );
    }
  }

  return (
    <Input
      type={isNumericField(row) ? 'number' : 'text'}
      value={row.value}
      onChange={(event) => onValueChange(event.target.value)}
      placeholder={t('specialRulesModal.valuePlaceholder')}
      data-testid={`${testIdPrefix}-value-input-${idx}`}
    />
  );
};
